import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CustomProvider } from 'rsuite';
import esES from 'rsuite/locales/es_ES';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { configuracionAPI } from './services/api';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import NotFoundPage from './components/NotFoundPage';
import AccesoDenegado from './components/AccesoDenegado';
import { useResetTitleOnRouteChange } from './hooks/useDocumentTitle';
import Sidebar from './components/Sidebar';
import CotizacionesList from './components/CotizacionesList';
import CotizacionForm from './components/CotizacionForm';
import CotizacionFormManual from './components/CotizacionFormManual';
import ErrorBoundary from './components/ErrorBoundary';
import CotizacionDetalle from './components/CotizacionDetalle';
import PlantillasList from './components/PlantillasList';
import PlantillaForm from './components/PlantillaForm';
import ClientesList from './components/ClientesList';
import ClienteDetalle from './components/ClienteDetalle';
import ClientesCotizaciones from './components/ClientesCotizaciones';
import ClienteCotizacionesDetalle from './components/ClienteCotizacionesDetalle';
import MisCotizaciones from './components/MisCotizaciones';
import GenerarCotizacionIA from './components/GenerarCotizacionIA';
import MetricasIA from './components/MetricasIA';
import NotificacionesPage from './components/NotificacionesPage';
import Topbar from './components/Topbar';
import ServiciosPanel from './components/ServiciosPanel';
import CategoriasPanel from './components/CategoriasPanel';
import CategoriasCostoFijoPanel from './components/CategoriasCostoFijoPanel';
import PeriodosPanel from './components/PeriodosPanel';
import PlanesPagoPanel from './components/PlanesPagoPanel';
import PersonalPanel from './components/PersonalPanel';
import CostosFijosPanel from './components/CostosFijosPanel';
import ConfiguracionPanel from './components/ConfiguracionPanel';
import DatosEmpresa from './components/DatosEmpresa';
import ConfiguracionIAPanel from './components/ConfiguracionIAPanel';
import UsuariosPanel from './components/UsuariosPanel';
import RolesPanel from './components/RolesPanel';
import LogsPanel from './components/LogsPanel';
import NumeracionesPanel from './components/NumeracionesPanel';
import AlertasPanel from './components/AlertasPanel';
import Dashboard from './components/Dashboard';
import Perfil from './components/Perfil';
import CommandPalette from './components/CommandPalette';
import { ConfirmProvider, TooltipProvider } from './components/ui';
import './styles.css';

// Wrapper para CotizacionForm con navegación.
// En modo edicion (isNew=false), hace un fetch previo para detectar si la
// cotizacion es manual — si lo es, renderiza el form manual en su lugar.
function CotizacionFormWrapper({ configuracion, onConfigUpdate, isNew }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [checkingType, setCheckingType] = useState(!isNew);
    const [esManual, setEsManual] = useState(false);

    useEffect(() => {
        if (isNew || !id) return;
        let cancelled = false;
        import('./services/api').then(({ cotizacionesAPI }) => {
            cotizacionesAPI.getById(id)
                .then(r => {
                    if (cancelled) return;
                    setEsManual(!!r.data?.es_manual);
                })
                .finally(() => { if (!cancelled) setCheckingType(false); });
        });
        return () => { cancelled = true; };
    }, [id, isNew]);

    if (checkingType) return <div className="loading">Cargando…</div>;
    if (esManual) return <CotizacionFormManual cotizacionId={id} />;

    const tipoCambio = parseFloat(configuracion.tipo_cambio) || 3.70;
    const igvRate = parseFloat(configuracion.igv_rate) || 0.18;
    const rentabilidad = configuracion.rentabilidad !== undefined && configuracion.rentabilidad !== null
        ? parseFloat(configuracion.rentabilidad)
        : 0.68;
    const rentabilidadCostoFijo = configuracion.rentabilidad_costo_fijo !== undefined && configuracion.rentabilidad_costo_fijo !== null
        ? parseFloat(configuracion.rentabilidad_costo_fijo)
        : 0.68;

    return (
        <ErrorBoundary
            label="No pudimos abrir el formulario de cotización"
            category={location.state?.aiData ? 'cotizacion-form:ia' : 'cotizacion-form'}
            onReset={() => navigate('/cotizaciones', { replace: true })}
        >
            <CotizacionForm
                cotizacionId={isNew ? null : id}
                tipoCambio={tipoCambio}
                igvRate={igvRate}
                rentabilidad={rentabilidad}
                rentabilidadCostoFijo={rentabilidadCostoFijo}
                configuracion={configuracion}
                aiData={location.state?.aiData || null}
                onSaved={() => navigate('/cotizaciones')}
                onCancel={() => navigate('/cotizaciones')}
            />
        </ErrorBoundary>
    );
}

// Wrapper para CotizacionesList con navegación
function CotizacionesListWrapper({ configuracion }) {
    const navigate = useNavigate();

    return (
        <CotizacionesList
            onEdit={(id) => navigate(`/cotizaciones/${id}`)}
            onNueva={() => navigate('/cotizaciones/nueva')}
            configuracion={configuracion}
        />
    );
}

// Guard para rutas con :id numerico. Si el segmento no es entero positivo,
// redirige a /404 antes de montar el componente — evita que hijos como
// CotizacionForm disparen fetch/lock/anexos con un ID basura.
function RequireNumericId({ param = 'id', children }) {
    const params = useParams();
    const value = params[param];
    if (!value || !/^\d+$/.test(value)) {
        return <Navigate to="/404" replace />;
    }
    return children;
}

// Protección de rutas por permiso (acepta string o array con lógica OR)
function ProtectedRoute({ permiso, children }) {
    const { tienePermiso } = useAuth();
    const permisos = Array.isArray(permiso) ? permiso : [permiso];
    const tieneAlguno = permisos.some(p => tienePermiso(p));
    if (!tieneAlguno) {
        return <AccesoDenegado />;
    }
    return children;
}

// Componente principal de la aplicacion
function AppContent() {
    const { isAuthenticated, loading: authLoading } = useAuth();
    // Restablecer document.title al DEFAULT en cada cambio de ruta. Las paginas
    // que quieren un titulo especifico (CotizacionDetalle, CotizacionForm, 404)
    // lo overridean despues con useDocumentTitle() en su propio efecto.
    useResetTitleOnRouteChange();

    // Estados
    const [configuracion, setConfiguracion] = useState({});
    const [loading, setLoading] = useState(true);
    const [sidebarExpanded, setSidebarExpanded] = useState(window.innerWidth >= 1024);
    const handleSidebarExpanded = useCallback((val) => setSidebarExpanded(val), []);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const handleToggleSidebar = useCallback(() => setSidebarExpanded(v => !v), []);

    // Cargar configuracion al autenticarse
    useEffect(() => {
        if (isAuthenticated) {
            cargarConfiguracion();
        }
    }, [isAuthenticated]);

    const cargarConfiguracion = async () => {
        try {
            const response = await configuracionAPI.getAll();
            setConfiguracion(response.data || {});
        } catch (err) {
            console.error('Error al cargar configuracion:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleConfigUpdate = (newConfig) => {
        setConfiguracion(newConfig);
    };

    // Si esta cargando autenticacion — pulso neutro centrado (sin texto)
    if (authLoading) {
        return (
            <div
                role="status"
                aria-label="Cargando sesión"
                aria-live="polite"
                className="min-h-screen flex items-center justify-center bg-bg motion-safe:animate-pulse motion-reduce:opacity-70"
            >
                <div className="h-2 w-24 rounded-full bg-line/60" />
            </div>
        );
    }

    // Si no esta autenticado, mostrar rutas publicas: login / forgot / reset.
    // /404 se renderiza limpio (sin layout). Cualquier otra ruta desconocida
    // cae a login (comportamiento historico — evita filtrar URLs internas).
    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/404" element={<NotFoundPage />} />
                <Route path="*" element={<Login />} />
            </Routes>
        );
    }

    // Nivel superior: /404 se muestra fullscreen sin Sidebar ni Topbar.
    // Cualquier otra ruta cae al layout autenticado (Sidebar + Topbar + main).
    return (
        <Routes>
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={
        <div className="relative min-h-screen flex bg-bg">
            <Sidebar configuracion={configuracion} expanded={sidebarExpanded} onExpandedChange={handleSidebarExpanded} />
            <CommandPalette />
            <div
                className={`
                    flex-1 min-h-screen flex flex-col min-w-0
                    transition-[margin-left,max-width] duration-300 ease-out
                    ${isMobile
                        ? 'ml-0 max-w-full'
                        : (sidebarExpanded ? 'ml-[260px] max-w-[calc(100%-260px)]' : 'ml-[56px] max-w-[calc(100%-56px)]')}
                `}
            >
                <Topbar
                    onMenuToggle={handleToggleSidebar}
                    showMenuButton={isMobile}
                    sidebarOffset={isMobile ? 0 : (sidebarExpanded ? 260 : 56)}
                />
                <main className="flex-1 min-w-0 max-w-full px-6 sm:px-8 pb-8">
                    {loading ? (
                        <div
                            role="status"
                            aria-label="Cargando"
                            aria-live="polite"
                            className="pt-6 motion-safe:animate-pulse motion-reduce:opacity-70"
                        >
                            <div className="h-7 w-48 rounded bg-line/60 mb-6" />
                            <div className="bg-surface border border-line rounded-xl shadow-soft p-6 mb-3.5">
                                <div className="h-5 w-40 rounded bg-line/60 mb-5" />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="h-16 rounded bg-line/40" />
                                    <div className="h-16 rounded bg-line/40" />
                                    <div className="h-16 rounded bg-line/40" />
                                </div>
                            </div>
                            <div className="bg-surface border border-line rounded-xl shadow-soft p-6">
                                <div className="h-5 w-32 rounded bg-line/60 mb-5" />
                                <div className="space-y-3">
                                    <div className="h-12 rounded bg-line/40" />
                                    <div className="h-12 rounded bg-line/40" />
                                    <div className="h-12 rounded bg-line/40" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route
                                path="/cotizaciones"
                                element={<ProtectedRoute permiso="gestion_cotizaciones"><CotizacionesListWrapper configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/cotizaciones/nueva"
                                element={
                                    <ProtectedRoute permiso="gestion_cotizaciones">
                                        <CotizacionFormWrapper
                                            configuracion={configuracion}
                                            onConfigUpdate={handleConfigUpdate}
                                            isNew={true}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/cotizaciones/nueva/manual"
                                element={
                                    <ProtectedRoute permiso="gestion_cotizaciones">
                                        <CotizacionFormManual />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/cotizaciones/clientes"
                                element={<ProtectedRoute permiso="gestion_cotizaciones"><ClientesCotizaciones /></ProtectedRoute>}
                            />
                            <Route
                                path="/cotizaciones/cliente/:id"
                                element={<RequireNumericId><ProtectedRoute permiso={["gestion_cotizaciones", "cotizaciones_asignadas"]}><ClienteCotizacionesDetalle configuracion={configuracion} /></ProtectedRoute></RequireNumericId>}
                            />
                            <Route
                                path="/cotizaciones/mis-cotizaciones"
                                element={<MisCotizaciones configuracion={configuracion} />}
                            />
                            <Route
                                path="/cotizaciones/generar-ia"
                                element={<ProtectedRoute permiso="usar_ia"><GenerarCotizacionIA /></ProtectedRoute>}
                            />
                            <Route
                                path="/metricas-ia"
                                element={<ProtectedRoute permiso="ver_metricas_ia"><MetricasIA /></ProtectedRoute>}
                            />
                            <Route
                                path="/notificaciones"
                                element={<NotificacionesPage />}
                            />
                            <Route
                                path="/cotizaciones/:id/detalle"
                                element={
                                    <RequireNumericId>
                                        <ProtectedRoute permiso={["gestion_cotizaciones", "cotizaciones_asignadas"]}>
                                            <CotizacionDetalle configuracion={configuracion} />
                                        </ProtectedRoute>
                                    </RequireNumericId>
                                }
                            />
                            <Route
                                path="/cotizaciones/:id"
                                element={
                                    <RequireNumericId>
                                        <ProtectedRoute permiso={["gestion_cotizaciones", "cotizaciones_asignadas"]}>
                                            <CotizacionFormWrapper
                                                configuracion={configuracion}
                                                onConfigUpdate={handleConfigUpdate}
                                                isNew={false}
                                            />
                                        </ProtectedRoute>
                                    </RequireNumericId>
                                }
                            />
                            <Route
                                path="/plantillas"
                                element={<ProtectedRoute permiso="gestion_plantillas"><PlantillasList /></ProtectedRoute>}
                            />
                            <Route
                                path="/plantillas/nueva"
                                element={<ProtectedRoute permiso="gestion_plantillas"><PlantillaForm configuracion={configuracion} /></ProtectedRoute>}
                            />
                            <Route
                                path="/plantillas/:id"
                                element={<RequireNumericId><ProtectedRoute permiso="gestion_plantillas"><PlantillaForm configuracion={configuracion} /></ProtectedRoute></RequireNumericId>}
                            />
                            <Route
                                path="/clientes"
                                element={<ProtectedRoute permiso="gestion_clientes"><ClientesList /></ProtectedRoute>}
                            />
                            <Route
                                path="/clientes/:id"
                                element={<RequireNumericId><ProtectedRoute permiso="gestion_clientes"><ClienteDetalle /></ProtectedRoute></RequireNumericId>}
                            />
                            <Route
                                path="/perfil"
                                element={<Perfil />}
                            />
                            <Route
                                path="/alertas"
                                element={
                                    <ProtectedRoute permiso="seguimientos">
                                        <div className="card">
                                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                                <h2>Alertas y Seguimiento</h2>
                                            </div>
                                            <AlertasPanel />
                                        </div>
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/servicios" element={<ProtectedRoute permiso="gestion_servicios"><ServiciosPanel /></ProtectedRoute>} />
                            <Route path="/servicios/categorias" element={<ProtectedRoute permiso="gestion_servicios"><CategoriasPanel /></ProtectedRoute>} />
                            <Route path="/servicios/periodos" element={<ProtectedRoute permiso="gestion_servicios"><PeriodosPanel /></ProtectedRoute>} />
                            <Route path="/personal" element={<ProtectedRoute permiso="gestion_personal"><PersonalPanel /></ProtectedRoute>} />
                            <Route path="/costos-fijos" element={<ProtectedRoute permiso="gestion_costos_fijos"><CostosFijosPanel /></ProtectedRoute>} />
                            <Route path="/costos-fijos/categorias" element={<ProtectedRoute permiso="gestion_costos_fijos"><CategoriasCostoFijoPanel /></ProtectedRoute>} />
                            <Route path="/costos-fijos/periodos" element={<ProtectedRoute permiso="gestion_costos_fijos"><PeriodosPanel /></ProtectedRoute>} />
                            <Route path="/pagos/planes" element={<ProtectedRoute permiso="gestion_planes_pago"><PlanesPagoPanel /></ProtectedRoute>} />
                            <Route
                                path="/configuracion"
                                element={<ProtectedRoute permiso="configuracion"><ConfiguracionPanel onConfigUpdate={handleConfigUpdate} /></ProtectedRoute>}
                            />
                            <Route
                                path="/datos-empresa"
                                element={<ProtectedRoute permiso="configuracion"><DatosEmpresa onConfigUpdate={handleConfigUpdate} /></ProtectedRoute>}
                            />
                            <Route
                                path="/configuracion-ia"
                                element={<ProtectedRoute permiso="configuracion"><ConfiguracionIAPanel /></ProtectedRoute>}
                            />
                            <Route
                                path="/configuracion-ia/:tab"
                                element={<ProtectedRoute permiso="configuracion"><ConfiguracionIAPanel /></ProtectedRoute>}
                            />
                            <Route path="/usuarios" element={<ProtectedRoute permiso="gestion_usuarios"><UsuariosPanel /></ProtectedRoute>} />
                            <Route path="/roles" element={<ProtectedRoute permiso="gestion_roles"><RolesPanel /></ProtectedRoute>} />
                            <Route path="/logs" element={<ProtectedRoute permiso="ver_logs_sistema"><LogsPanel /></ProtectedRoute>} />
                            <Route path="/numeraciones" element={<ProtectedRoute permiso="configuracion"><NumeracionesPanel /></ProtectedRoute>} />
                            {/* Cualquier ruta desconocida → /404 fullscreen sin layout */}
                            <Route path="*" element={<Navigate to="/404" replace />} />
                        </Routes>
                    )}
                </main>

                <div className="px-4 sm:px-6 lg:px-8 pb-[env(safe-area-inset-bottom)]">
                    <footer className="end">
                        <div className="endGlow" />
                        <p className="endText">
                            {configuracion.empresa_nombre || 'Alma Industria Creativa'} - Don Presupuesto
                        </p>
                        <p className="endCopyright">
                            &copy; {new Date().getFullYear()} - <a href="https://almaquinta.com/" target="_blank" rel="noopener noreferrer">Alma Quinta</a>
                        </p>
                    </footer>
                </div>
            </div>
        </div>
            } />
        </Routes>
    );
}

// Bridge: lee el tema del ThemeContext y pasa el theme a rsuite CustomProvider.
// Asi DateRangePicker, DatePicker, Sidenav y demas componentes de rsuite respetan light/dark.
function RsuiteThemed({ children }) {
    const { theme } = useTheme();
    return (
        <CustomProvider locale={esES} theme={theme}>
            {children}
        </CustomProvider>
    );
}

// Toaster theme-aware: lee el tema y aplica estilos coherentes.
function ThemedToaster() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    return (
        <Toaster
            position="bottom-center"
            toastOptions={{
                duration: 3000,
                style: {
                    background: isDark ? '#1a1f3c' : '#ffffff',
                    color: isDark ? '#ffffff' : '#0f172a',
                    border: `1px solid ${isDark ? 'rgba(140, 220, 255, 0.25)' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    boxShadow: isDark ? 'none' : '0 4px 12px rgba(15, 23, 42, 0.1)'
                },
                success: {
                    iconTheme: {
                        primary: '#10b981',
                        secondary: isDark ? '#1a1f3c' : '#ffffff'
                    }
                },
                error: {
                    iconTheme: {
                        primary: '#ef4444',
                        secondary: isDark ? '#1a1f3c' : '#ffffff'
                    }
                }
            }}
        />
    );
}

// Wrapper con ThemeProvider, AuthProvider y BrowserRouter
export default function App() {
    return (
        <ThemeProvider>
            <RsuiteThemed>
                <BrowserRouter>
                    <AuthProvider>
                        <TooltipProvider>
                            <ConfirmProvider>
                                <AppContent />
                                <ThemedToaster />
                            </ConfirmProvider>
                        </TooltipProvider>
                    </AuthProvider>
                </BrowserRouter>
            </RsuiteThemed>
        </ThemeProvider>
    );
}
